/*
    The shaping behind the Holdout Test Results report (MPN-1688).

    The server does every calculation. Nothing here recomputes a rate, a lift or a p-value --
    if this file did its own arithmetic the page could disagree with the CSV, and with the
    Google Sheet this replaces, and nobody would know which was right.

    What lives here is everything a table needs and a component should not be doing inline:
    filtering, formatting, and turning rows into CSV. Extracted so it can be tested without a
    DOM, the same way the revenue-by-journey-step rows are.
*/

export type ConfidenceTier = "proven" | "directional" | "insufficient" | "caution";

export interface HoldoutRow {
    launch_id: number;
    journey_step_id: number | null;
    client: string;
    client_id: number;
    campaign: string;
    /** Only so the Campaign column can be followed. `launch_id` addresses no page in the app,
        which made Campaign the one identifier on this report that led nowhere. */
    engagement_id: number | null;
    campaign_status: string;
    use_case: string;
    /** The words for `use_case`. The code is kept for filtering and the CSV; this is what a
        reader should ever see, because `leads_or_inquiries` on a board slide is a storage
        detail rather than a finding. */
    use_case_label: string;
    funnel_stage: string;
    funnel_stage_label: string;
    target_action: string;
    /** How many (step, dimension) cells were summed into this row. The rollup is correct and
        invisible: a step with four terms under it looks exactly like one with a single term,
        and somebody reconciling against a per-dimension export sees a figure they cannot
        account for. */
    dimensions: number;
    /** The dimension this row is, since MPN-2076 item 4 split the grain. Empty on a step that
        has none, which is most of them. */
    dimension_name: string;
    journey_step_dimension_id: number | null;
    days_running: number | null;
    treatment_size: number;
    treatment_conversions: number;
    control_size: number;
    control_conversions: number;
    treatment_rate: number | null;
    control_rate: number | null;
    absolute_lift: number | null;
    absolute_lift_lower: number | null;
    absolute_lift_upper: number | null;
    relative_lift: number | null;
    relative_lift_lower: number | null;
    relative_lift_upper: number | null;
    p_value: number | null;
    tier: ConfidenceTier;
    srm_check: string | null;
    statistical_power: string | null;
    /** Remembered server-side: a fact about a day that has passed, not a live calculation. */
    date_reached: string | null;
    last_calculated: string | null;
}

/*
    Every filter the By Campaign tab offers (MPN-2045, Holly's updated prototype).

    MULTI-SELECT, not one value each. The prototype moved these to the platform's chip
    filters -- the same ones the Engagements list uses -- and a chip opens a checklist, so
    "Status: Live or Paused" is a question somebody can now ask. A single string per field
    could not express it.

    One free-text `q` across client, campaign and Target Action, rather than three boxes. It
    is one question ("where is Northbridge") asked three ways, and three boxes made somebody
    pick which one to type in before they could ask it.
*/
export interface HoldoutFilters {
    /** Searches client, campaign and Target Action together. */
    q?: string;
    campaignStatus?: string[];
    useCase?: string[];
    funnelStage?: string[];
    tier?: string[];
    srm?: string[];
    power?: string[];
}

/** The chip filters, in the order the prototype shows them. */
export const CHIP_FILTERS: { key: ChipKey; label: string; field: keyof HoldoutRow }[] = [
    { key: "campaignStatus", label: "Status", field: "campaign_status" },
    { key: "useCase", label: "Use Case", field: "use_case_label" },
    { key: "funnelStage", label: "Funnel Stage", field: "funnel_stage_label" },
    { key: "tier", label: "Confidence Tier", field: "tier" },
    { key: "srm", label: "SRM Check", field: "srm_check" },
    { key: "power", label: "Statistical Power", field: "statistical_power" },
];

export type ChipKey = "campaignStatus" | "useCase" | "funnelStage" | "tier" | "srm" | "power";

/** Whether anything is narrowing the table. Drives "Clear Filters", which the prototype only
    shows once there is something to clear. */
export function anyFilterActive(filters: HoldoutFilters): boolean {
    if ((filters.q ?? "").trim()) return true;
    return CHIP_FILTERS.some((chip) => (filters[chip.key] ?? []).length > 0);
}

/**
 * Rows matching every filter that is set.
 *
 * AND across the chips, OR within one, which is what a checklist means: "Status: Live or
 * Paused" and "Tier: Proven" is one question with two parts, not two questions.
 *
 * The search is a case-insensitive substring across the three identity columns, because
 * somebody typing "north" is looking for Northbridge and should not have to know whether it
 * was entered with a suffix.
 */
export function filterRows(rows: HoldoutRow[], filters: HoldoutFilters): HoldoutRow[] {
    const term = (filters.q ?? "").trim().toLowerCase();
    return rows.filter((row) => (
        matchesSearch(row, term)
        && oneOf(row.campaign_status, filters.campaignStatus)
        && oneOf(row.use_case_label || row.use_case, filters.useCase)
        && oneOf(row.funnel_stage_label, filters.funnelStage)
        && oneOf(row.tier, filters.tier)
        && oneOf(row.srm_check, filters.srm)
        && oneOf(row.statistical_power, filters.power)
    ));
}

function matchesSearch(row: HoldoutRow, term: string): boolean {
    if (!term) return true;
    return [row.client, row.campaign, row.target_action, row.dimension_name]
        .some((value) => (value || "").toLowerCase().includes(term));
}

/** Empty means "any", which is what an untouched chip reads as. */
function oneOf(value: string | null, wanted?: string[]): boolean {
    if (!wanted || wanted.length === 0) return true;
    return wanted.includes(value || "");
}

/** The distinct values of a column, for its dropdown. Sorted, blanks dropped. */
export function optionsFor(rows: HoldoutRow[], key: keyof HoldoutRow): string[] {
    const seen = new Set<string>();
    rows.forEach((row) => {
        const value = row[key];
        if (typeof value === "string" && value) seen.add(value);
    });
    return [...seen].sort();
}

const CSV_COLUMNS: { header: string; value: (row: HoldoutRow) => string }[] = [
    { header: "Client", value: (r) => r.client },
    { header: "Campaign", value: (r) => r.campaign },
    { header: "Campaign Status", value: (r) => r.campaign_status },
    { header: "Use Case", value: (r) => r.use_case_label || r.use_case },
    // The code as well as the words, so an export can be joined back to the data.
    { header: "Use Case Code", value: (r) => r.use_case },
    { header: "Funnel Stage", value: (r) => r.funnel_stage_label },
    { header: "Target Action", value: (r) => r.target_action },
    { header: "Dimension", value: (r) => r.dimension_name },
    { header: "Days Running", value: (r) => num(r.days_running) },
    { header: "Treatment Size", value: (r) => num(r.treatment_size) },
    { header: "Treatment Conversions", value: (r) => num(r.treatment_conversions) },
    { header: "Treatment Rate %", value: (r) => num(r.treatment_rate) },
    { header: "Control Size", value: (r) => num(r.control_size) },
    { header: "Control Conversions", value: (r) => num(r.control_conversions) },
    { header: "Control Rate %", value: (r) => num(r.control_rate) },
    { header: "Absolute Lift (pp)", value: (r) => num(r.absolute_lift) },
    { header: "Absolute Lift Lower", value: (r) => num(r.absolute_lift_lower) },
    { header: "Absolute Lift Upper", value: (r) => num(r.absolute_lift_upper) },
    { header: "Relative Lift %", value: (r) => num(r.relative_lift) },
    { header: "Relative Lift Lower", value: (r) => num(r.relative_lift_lower) },
    { header: "Relative Lift Upper", value: (r) => num(r.relative_lift_upper) },
    { header: "Confidence Tier", value: (r) => r.tier },
    { header: "P-Value", value: (r) => num(r.p_value) },
    { header: "SRM Check", value: (r) => r.srm_check || "" },
    { header: "Statistical Power", value: (r) => r.statistical_power || "" },
    { header: "Date Reached", value: (r) => r.date_reached || "" },
    { header: "Last Calculated", value: (r) => r.last_calculated || "" },
];

/**
 * The visible rows as CSV.
 *
 * Takes rows rather than fetching its own, so what is exported is exactly what is on screen
 * -- the acceptance criteria ask for the current filters to be respected, and a second fetch
 * would quietly export everything.
 *
 * Every field is quoted. A campaign called "Spring Re-enroll, Cohort 2" would otherwise become
 * two columns, and the row after it would be misaligned for the rest of the file.
 */
export function toCsv(rows: HoldoutRow[]): string {
    const lines = [CSV_COLUMNS.map((column) => quote(column.header)).join(",")];
    rows.forEach((row) => {
        lines.push(CSV_COLUMNS.map((column) => quote(column.value(row))).join(","));
    });
    return lines.join("\n");
}

function quote(value: string): string {
    return `"${String(value ?? "").replace(/"/g, '""')}"`;
}

function num(value: number | null | undefined): string {
    return value === null || value === undefined ? "" : String(value);
}

/**
 * The dash every empty cell uses: an em dash, not a hyphen.
 *
 * A hyphen beside a column of numbers reads as a minus sign, which on a report whose whole
 * subject is "did this go up or down" is the one ambiguity worth spending a character on
 * (Holly, QA, 6 Oct 2026).
 */
export const EMPTY = "\u2014";

/**
 * One decimal, with negative zero flattened.
 *
 * `(-0.04).toFixed(1)` is `"-0.0"` in JavaScript, so a lift of essentially nothing rendered
 * as "-0.0pp" -- a minus sign in front of a zero, on a table people read to decide whether a
 * campaign worked.
 */
function oneDecimal(value: number): string {
    const fixed = value.toFixed(1);
    return fixed === "-0.0" ? "0.0" : fixed;
}

/** Percentage points, to one decimal. Null reads as an em dash rather than "null". */
export function pp(value: number | null | undefined): string {
    return value === null || value === undefined ? EMPTY : `${oneDecimal(value)}pp`;
}

export function pct(value: number | null | undefined): string {
    return value === null || value === undefined ? EMPTY : `${oneDecimal(value)}%`;
}

/**
 * A conversion rate, to TWO decimals (MPN-2076).
 *
 * One decimal caused two problems, and the first is the one that cost trust: it made the
 * relative lift beside it look miscalculated. Treatment 1.2% against Control 0.4% reads as a
 * 200% lift, but the page showed 189.3% -- because the lift is computed from the full-precision
 * figures while the rates were rounded for display. The arithmetic was right and the display
 * made it look wrong, which on this report is the expensive kind of wrong.
 *
 * The second is plainer: a real rate of 0.03% rounded to "0.0%", which reads as nobody
 * converting when somebody did.
 *
 * Below the two decimals can show, it says "<0.01%" rather than "0.00%", for the same reason
 * `pValue` says "<0.001" -- a rounded zero claims something the number does not. A true zero
 * still reads "0.00%", because that one IS nobody.
 *
 * The server sends rates rounded to four decimal places, so this precision is really there.
 */
export function rate(value: number | null | undefined): string {
    if (value === null || value === undefined) return EMPTY;
    if (value > 0 && value < 0.005) return "<0.01%";
    const fixed = value.toFixed(2);
    return `${fixed === "-0.00" ? "0.00" : fixed}%`;
}

/**
 * Relative lift, which is undefined rather than unknown when nobody in the control converted.
 *
 * `(t - c) / c` has no meaning at c = 0, and the spec calls for "n/a" there. A dash would say
 * "we have not measured this", which is a different and more hopeful claim than "this
 * quantity does not exist" -- and it is exactly the case the Caution tier exists to flag.
 */
export function relativeLift(
    value: number | null | undefined, controlRate: number | null | undefined,
): string {
    if (controlRate === 0) return "n/a";
    return pct(value);
}

/**
 * A p-value, to three decimals.
 *
 * Shown as "<0.001" rather than "0.000" below that, because a rounded zero reads as certainty
 * and no p-value is ever zero.
 */
export function pValue(value: number | null | undefined): string {
    if (value === null || value === undefined) return EMPTY;
    return value < 0.001 ? "<0.001" : value.toFixed(3);
}

/**
 * A date as a person writes it: "Jul 14, 2026".
 *
 * Was `toISOString().slice(0, 10)`, which is both machine-shaped and wrong near midnight --
 * it converts to UTC first, so an evening calculation rendered as the previous day.
 */
export function shortDate(value: string | null | undefined): string {
    const parsed = parseStamp(value);
    if (!parsed) return EMPTY;
    return parsed.toLocaleDateString("en-US", {
        month: "short", day: "numeric", year: "numeric",
    });
}

/**
 * Parse a server value without moving the day.
 *
 * `date_reached` arrives as "2026-07-14". `new Date()` reads a bare date as UTC midnight, so
 * rendering it in any negative offset shows the 13th. The old code hid this by formatting
 * back in UTC, which then shifted real timestamps the other way instead. A date is a date:
 * build it in local time so it is the day the server meant, and leave timestamps alone.
 */
function parseStamp(value: string | null | undefined): Date | null {
    if (!value) return null;
    const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    const parsed = dateOnly
        ? new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
        : new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/**
 * A timestamp for the freshness line: "Sep 5, 2026, 6:00 AM".
 *
 * The report recalculates daily and the time is how somebody knows whether today's run has
 * landed yet. Dropping it made "Data as of" answer a question nobody was asking.
 */
export function stamp(value: string | null | undefined): string {
    const parsed = parseStamp(value);
    if (!parsed) return EMPTY;
    const date = parsed.toLocaleDateString("en-US", {
        month: "short", day: "numeric", year: "numeric",
    });
    const time = parsed.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
    return `${date}, ${time}`;
}

/** The same stamp without the year, for the per-row Last Calculated column. */
export function shortStamp(value: string | null | undefined): string {
    const parsed = parseStamp(value);
    if (!parsed) return EMPTY;
    const date = parsed.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    const time = parsed.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
    return `${date}, ${time}`;
}

export const TIER_LABEL: Record<string, string> = {
    proven: "Proven",
    directional: "Directional",
    insufficient: "Insufficient data",
    caution: "Caution",
};

/**
 * How every stored vocabulary value reads to a person.
 *
 * The filter chips are built from the values actually present in the rows, so without this
 * they offered "LIVE", "pass" and "low" -- storage spellings, in a control somebody reads
 * before they read the table. Superset of TIER_LABEL rather than a replacement, because the
 * tier labels are also used on their own.
 *
 * An unknown value falls through as itself. A status added server-side should look unstyled
 * here, not vanish from the filter that is meant to find it.
 */
export const VALUE_LABEL: Record<string, string> = {
    ...TIER_LABEL,
    LIVE: "Live", ENDED: "Ended", PAUSED: "Paused", DRAFT: "Draft", SCHEDULED: "Scheduled",
    pass: "Pass", fail: "Fail",
    low: "Low", sufficient: "Sufficient",
};

export function valueLabel(value: string): string {
    return VALUE_LABEL[value] ?? value;
}

/**
 * A row left out of the report, with its statistics and the reason it was taken out.
 *
 * It carries a tier like any other row, deliberately. Excluding a Proven result is a very
 * different act from excluding one with insufficient data, and a table that could not show
 * which had happened would be one somebody could quietly use to drop inconvenient results.
 */
export interface ExcludedRow extends HoldoutRow {
    reason: string;
    reason_label: string;
    detail: string;
    excluded_by: string | null;
    excluded_on: string | null;
    /** The whole campaign was excluded, not just this target action. */
    whole_campaign: boolean;
}

/** What the page-level controls send to the server. */
export interface ReportScope {
    start?: string;
    end?: string;
    organizationId?: number;
}

/**
 * The scope as query parameters, with the blanks left out.
 *
 * Separate from the controls so an empty date box is an absent parameter rather than
 * `start=`, which the server would try to parse as a date and reject. A half-entered range is
 * the normal state of a date picker somebody is still using.
 */
export function toQuery(scope: ReportScope): Record<string, string> {
    const query: Record<string, string> = {};
    if (isDate(scope.start)) query.start = scope.start as string;
    if (isDate(scope.end)) query.end = scope.end as string;
    if (scope.organizationId) query.organization_id = String(scope.organizationId);
    return query;
}

/** A complete date, not a partial one. A date input reports "2026-09" mid-entry. */
function isDate(value?: string): boolean {
    return /^\d{4}-\d{2}-\d{2}$/.test((value || "").trim());
}

/**
 * Whether a date range is back to front.
 *
 * Worth saying out loud rather than sending: the server would return nothing, and an empty
 * report reads as "no campaigns are in holdout testing", which is a different and alarming
 * claim from "you typed the dates the wrong way round".
 */
export function rangeIsBackwards(scope: ReportScope): boolean {
    if (!isDate(scope.start) || !isDate(scope.end)) return false;
    return (scope.start as string) > (scope.end as string);
}

/*
    Column lenses.

    Twenty columns at once is the shape that makes somebody scroll sideways looking for the
    one they wanted. The prototype's answer is three curated sets, and it is a good one: most
    readings of this report are "did it work" (Results), a minority are "should I trust this"
    (Diagnostics), and Full is for the export-adjacent case.

    Client, Campaign and Target Action are pinned into every lens. They are how you know which
    row you are looking at, so no lens may take them away.
*/
export type Lens = "results" | "diagnostics" | "full";

export interface ColumnGroup {
    key: string;
    label: string;
    align: "left" | "right";
    /** Sub-headings shown under the group label, e.g. Treatment / Control. */
    parts?: string[];
    /** The definition behind each part, where the two halves mean different things.
        Treatment Size and Control Size are not one definition with two columns: one is the
        population minus the holdout, the other is the holdout. A single icon on the group
        would point at whichever was written first and quietly mislead about the other. */
    partColumns?: string[];
    /** The two parts render in ONE cell rather than two columns ("2,397 / 598", or a lift with
        its interval stacked under it). Declared here rather than in the table so the column
        COUNT cannot disagree with what is drawn -- it is the figure beside "Showing 12 of 40
        rows", and a count that is two too high is worse than no count at all. */
    paired?: boolean;
    lenses: Lens[];
}

const RESULTS: Lens[] = ["results", "full"];
const DIAGNOSTIC: Lens[] = ["diagnostics", "full"];
const FULL_ONLY: Lens[] = ["full"];

export const COLUMN_GROUPS: ColumnGroup[] = [
    { key: "campaign_status", label: "Campaign Status", align: "left", lenses: RESULTS },
    { key: "use_case", label: "Use Case", align: "left", parts: ["Use Case", "Funnel Stage"],
      lenses: FULL_ONLY },
    { key: "days_running", label: "Days Running", align: "right", lenses: DIAGNOSTIC },
    { key: "size", label: "Size", align: "right", parts: ["Treatment", "Control"],
      partColumns: ["treatment_size", "control_size"], paired: true, lenses: DIAGNOSTIC },
    // The numerator, next to the rate it produces (MPN-2076). It was in the CSV and not on
    // screen, so anybody reconciling the two had to export to see the count behind a
    // percentage -- on the lens whose whole job is showing everything.
    { key: "conversions", label: "Conversions", align: "right",
      parts: ["Treatment", "Control"],
      partColumns: ["treatment_conversions", "control_conversions"],
      paired: true, lenses: FULL_ONLY },
    { key: "conversion_rate", label: "Conversion Rate", align: "right",
      parts: ["Treatment", "Control"],
      partColumns: ["treatment_rate", "control_rate"], lenses: FULL_ONLY },
    { key: "absolute_lift", label: "Absolute Lift", align: "right",
      parts: ["Lift", "95% CI"], paired: true, lenses: RESULTS },
    { key: "relative_lift", label: "Relative Lift", align: "right",
      parts: ["Lift", "95% CI"], paired: true, lenses: RESULTS },
    { key: "tier", label: "Confidence Tier", align: "left", lenses: RESULTS },
    { key: "p_value", label: "P-value", align: "right", lenses: RESULTS },
    { key: "date_reached", label: "Date Reached", align: "right", lenses: RESULTS },
    { key: "srm_check", label: "SRM Check", align: "left", lenses: DIAGNOSTIC },
    { key: "statistical_power", label: "Statistical Power", align: "left", lenses: DIAGNOSTIC },
    { key: "last_calculated", label: "Last Calculated", align: "right", lenses: DIAGNOSTIC },
];

/** The groups a lens shows, in table order. Client/Campaign/Target Action are pinned and
    never appear here, because no lens is allowed to drop them. */
export function groupsFor(lens: Lens): ColumnGroup[] {
    return COLUMN_GROUPS.filter((group) => group.lenses.includes(lens));
}

/** The number of PINNED identity columns every lens carries: Client, Campaign, Target Action. */
const PINNED_COLUMNS = 3;

/**
 * How many columns a lens puts on screen.
 *
 * Counted from the groups rather than written down, so a group added to COLUMN_GROUPS changes
 * the figure with it. A group with `parts` is two columns, not one, which is why this cannot
 * be `groupsFor(lens).length` -- Size and Conversion Rate each occupy two.
 *
 * Shown beside the row count so "Showing 12 of 40 rows" says what is being hidden sideways as
 * well as downwards: switching from Results to Full is otherwise a silent change in what an
 * export will contain (Holly, QA, 6 Oct 2026).
 */
export function columnCount(lens: Lens): number {
    return groupsFor(lens).reduce(
        (total, group) => total + columnsIn(group), PINNED_COLUMNS,
    );
}

/** How many table cells a group occupies. Two parts are two columns unless the group is
    `paired`, in which case they share one. */
export function columnsIn(group: ColumnGroup): number {
    if (!group.parts || group.paired) return 1;
    return group.parts.length;
}

/**
 * Treatment and control size as one cell: "2,397 / 598".
 *
 * Two columns of counts with identical headings ("Treatment", "Control") under a single Size
 * heading reads as one number per row until you notice the pair. Together, separated, with
 * thousands separators, it reads as the split it is -- and the split is the thing somebody
 * checking an SRM flag is looking at.
 */
export function sizePair(treatment: number, control: number): string {
    return `${treatment.toLocaleString()} / ${control.toLocaleString()}`;
}

/**
 * The grain, counted from the rows on screen.
 *
 * "One row per campaign and Target Action" is true and still leaves somebody counting rows and
 * wondering why Northbridge appears twice. Saying how many distinct Target Actions are in play
 * answers that before it is asked.
 */
export function grainNote(rows: HoldoutRow[]): string {
    const campaigns = new Set(rows.map((row) => row.launch_id)).size;
    const actions = new Set(rows.map((row) => row.target_action)).size;
    const campaignWord = campaigns === 1 ? "campaign" : "campaigns";
    const actionWord = actions === 1 ? "Target Action" : "Target Actions";
    return `${campaigns} ${campaignWord} \u00b7 ${actions} ${actionWord} \u00b7 one row each`;
}

/** A confidence interval as one cell. Null bounds read as a dash, never as "null to null". */
export function interval(
    lower: number | null | undefined, upper: number | null | undefined, unit: "pp" | "%",
): string {
    if (lower === null || lower === undefined || upper === null || upper === undefined) {
        return EMPTY;
    }
    // The unit was being computed and then thrown away -- both branches returned "". A bare
    // "1.2 to 3.4" beside a column of percentages and a column of percentage points leaves
    // the reader to guess which one they are looking at.
    const suffix = unit === "pp" ? "pp" : "%";
    return `${oneDecimal(lower)}${suffix} to ${oneDecimal(upper)}${suffix}`;
}

/*
    Date range presets.

    Resolved against a fixed "today" the caller passes in rather than reading the clock here,
    so the same call twice gives the same answer and the tests do not depend on the day they
    are run.
*/
export type PresetKey = "30" | "90" | "qtd" | "ytd" | "all" | "custom";

export const PRESETS: { key: PresetKey; label: string }[] = [
    { key: "30", label: "Last 30 Days" },
    { key: "90", label: "Last 90 Days" },
    { key: "qtd", label: "Quarter to Date" },
    { key: "ytd", label: "Year to Date" },
    { key: "all", label: "All Time" },
];

/** The range a preset means. "All time" is an absent range, not a very old start date. */
export function presetRange(key: PresetKey, today: Date): { start?: string; end?: string } {
    if (key === "all" || key === "custom") return {};
    const end = iso(today);
    if (key === "30") return { start: iso(daysBefore(today, 29)), end };
    if (key === "90") return { start: iso(daysBefore(today, 89)), end };
    if (key === "qtd") {
        const quarterStart = Math.floor(today.getMonth() / 3) * 3;
        return { start: iso(new Date(today.getFullYear(), quarterStart, 1)), end };
    }
    return { start: iso(new Date(today.getFullYear(), 0, 1)), end };
}

function daysBefore(date: Date, days: number): Date {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate() - days);
}

function iso(date: Date): string {
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${date.getFullYear()}-${month}-${day}`;
}

/** How the chosen range reads in the header. */
export function rangeLabel(scope: ReportScope, preset: PresetKey): string {
    if (preset === "all") return "All time";
    const named = PRESETS.find((option) => option.key === preset);
    if (named) return named.label;
    if (scope.start && scope.end) return `${scope.start} to ${scope.end}`;
    if (scope.start) return `From ${scope.start}`;
    if (scope.end) return `Up to ${scope.end}`;
    return "All time";
}

/**
 * The freshness of the report, from the rows themselves.
 *
 * The latest, not the earliest: every client's file lands at its own time, and the question
 * the note answers is "how current is this page", not "is any of it stale".
 */
export function dataAsOf(rows: HoldoutRow[]): string | null {
    let latest: string | null = null;
    rows.forEach((row) => {
        if (row.last_calculated && (!latest || row.last_calculated > latest)) {
            latest = row.last_calculated;
        }
    });
    return latest;
}

/** What the active filters say, in words, for the empty state. */
export function describeFilters(filters: HoldoutFilters): string {
    const parts: string[] = [];
    const term = (filters.q ?? "").trim();
    if (term) parts.push(`a search for "${term}"`);
    CHIP_FILTERS.forEach((chip) => {
        const chosen = filters[chip.key] ?? [];
        if (!chosen.length) return;
        // "or" inside a chip, "and" between them -- the same words the chips themselves use,
        // so the empty state reads back the question somebody asked.
        const labelled = chosen.map(valueLabel);
        parts.push(`${chip.label} ${labelled.join(" or ")}`);
    });
    return parts.join(" and ");
}

